import { useState, useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import _ from 'lodash';

import schema, { getYupValidationErrors } from '@schemas';

/**
 * @param {*} initialValues initial values of your form
 * @param {*} pristineDefault false if you want to validate the form values 'onChange' before submitting once
 *
 * @returns {Object} formValues - The current values of your form
 * @returns {Function} updateValue - Updates 1 value directly to your state (no validation)
 * @returns {Function} handleInputChange - Function to use when using onChange event prop
 * @returns {Function} reset - set initialValues to formValues
 * @returns {Function} isValid - Validates the form state and returns true/false
 * @returns {Object} errors - Key/Value strcuture to show errors
 * @returns {Boolean} isInitial - false if the initial state hasn't changed
 * @example
 * const { formValues, handleInputChange, isValid, errors, isInitial } = useForm({
 *   doc_number,
 * });
 *
 * const handleSubmit = () => {
 *   if (!isValid()) return '';
 *   // TODO: SOMETHING
 * };
 *
 * <Input
 *   id="doc_number"
 *   testId="doc_number-testId"
 *   label={t('editDocumentNumber.document_type')}
 *   value={formValues.doc_number}
 *   onChange={handleInputChange}
 *   error={!!errors.doc_number}
 *   caption={errors.doc_number}
 * />
 *
 * <Button
 *   id="btn-submit"
 *   testId="btn-submit-testId"
 *   onClick={handleSubmit}
 *   disabled={isInitial()}
 * >
 *   {t('editDocumentNumber.submitbutton')}
 * </Button>
 */
const useForm = (initialValues = {}, pristineDefault = true) => {
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const yupValidator = useCallback(() => schema(Object.keys(formValues)), [initialValues]);
  const [pristine, setPristine] = useState(pristineDefault);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleInputChange = useCallback((e) => {
    let value = e;
    let { target } = e;

    if (typeof e === 'object') {
      value = e.target.value;
      target = { id: e.target.id || null };
    }

    setFormValues({
      ...formValues,
      [target.id]: value,
    });

    // eslint-disable-next-line no-use-before-define
    if (!pristine) isValid([target.id], target.value || value);
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isInitial = useCallback(() => _.isEqual(initialValues, formValues), [initialValues]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateValue = useCallback((key, value) => {
    setFormValues((state) => ({
      ...state,
      [key]: value,
    }));
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const reset = useCallback(() => {
    setFormValues(initialValues);
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isValid = useCallback((key, value) => {
    try {
      if (pristine) setPristine(false);
      yupValidator().validateSync(
        { ...formValues, [key]: value },
        {
          stripUnknown: true,
          abortEarly: false,
        }
      );
      setErrors({});
      return true;
    } catch (error) {
      const yupErrors = getYupValidationErrors(error, t);
      setErrors(yupErrors);
      return false;
    }
  });

  return {
    formValues,
    updateValue,
    handleInputChange,
    reset,
    isValid,
    errors,
    setErrors,
    isInitial,
  };
};

export default useForm;
